// React
import { memo, useCallback, useMemo, useState } from "react";
// Third Party
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash-es/cloneDeep";

// MUI
import { Button, Divider, Grid, Stack, Typography } from "@mui/material";

// Components
import CollapseArea from "@Layouts/components/Basics/CollapseArea";
import SummaryTable from "@DataPages/components/Inventory/Tables/Reconciliation/SummaryTable/SummaryTable.tsx";
import UnitsOnLotTable from "@DataPages/components/Inventory/Tables/Reconciliation/UnitsOnLotTable";
import ReconciliationCollapsableSection from "@DataPages/components/Inventory/Widgets/Reconciliation/ReconciliationCollapsableSection";
import UnitsOnScheduleTable from "@DataPages/components/Inventory/Tables/Reconciliation/UnitsOnScheduleTable";
import UnitsOnMfgTable from "@DataPages/components/Inventory/Tables/Reconciliation/UnitsOnMfgTable";
import OnScheduleNotOnLotTable from "@DataPages/components/Inventory/Tables/Reconciliation/OnScheduleNotOnLotTable";
import OnLotNotScheduledTable from "@DataPages/components/Inventory/Tables/Reconciliation/OnLotNotScheduledTable";
import ReconciliationSkeleton from "@DataPages/components/Inventory/Layout/Reconciliation/ReconciliationSkeleton";
import SearchInput from "@/Layouts/components/Basics/SearchInput";
import ReconcileNowButton from "../Tables/Reconciliation/components/ReconcileNowButton";
import OnLotNotOnMfgTable from "../Tables/Reconciliation/OnLotNotOnMfgTable";
import OnMfgNotOnLotTable from "../Tables/Reconciliation/OnMfgNotOnLotTable";
import UnitsOffLotTable from "../Tables/Reconciliation/UnitsOffLotTable";
import ExportReconciliationDialog from "../Widgets/Reconciliation/ExportReconciliationDialog";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import useReconciliationSubscriptions from "../hooks/useReconciliationSubscriptions";

// APIs
import {
	getReconciliationData,
	getReconciliationStatus,
	getReconciliationReasons,
	getReconciliationResolutions,
} from "@DataPages/api/inventory/reconciliation/queries";
import { exportReconciliation, performReconciliation } from "@DataPages/api/inventory/reconciliation/mutations";
import { getReconciliationSummary } from "@DataPages/api/inventory/queries";

// Types
import { IPayPeriod } from "@Common/api/types/CommissionCycle";
import { IReconciliationStatus, ISummaryDetail } from "@DataPages/types/inventory/models/reconciliation";
import { MemoDataReconciliation, TReconciliationType } from "../types/reconciliation";
import { IPageParam } from "@/Common/api/types/pagination";

// Helpers
import { debounceFn } from "@/Common/helpers/functions";
import {
	buildTextForUnreconciled,
	downloadFiles,
	filterByProperties,
	handleOpenCollapse,
	reconciliationFilterByType,
} from "../helpers/reconciliation";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";
import { and, or } from "@Common/helpers/sonar";

// Constants
import { DEBOUNCE_DELAY_500 } from "@/Constants/debounce";
import {
	ALL_FILTERS,
	NO_PREOWNED_FILTER,
	REASONS_RESOLUTION_PROPERTIES,
	ReconciliationStatus,
	UNITS_ON_MFG_FILTER,
	UNITS_ON_SCHEDULE_FILTER,
	VEHICLE_PROPERTIES,
} from "../constants/reconciliation";

// Hooks
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import useReconciliationStatusSubscription from "../hooks/useReconciliationStatusSubscription";
import useReconciliationSummarySubscription from "../hooks/useReconciliationSummarySubscription";
import { useFilteredData } from "../hooks/useFilteredData";
import { InventoryTab } from "../constants";
import { useSelectionContext } from "@/Common/context/selection/SelectionContext";

type ReconciliationProps = Readonly<{
	selectedPayPeriod: IPayPeriod;
	locationId: string;
}>;

// Styles
const collapseAreaSx = {
	paddingY: 3,
	paddingLeft: 2,
};

// Memo components
const SummaryTableMemo = memo(SummaryTable);
const ReconciliationCollapsableSectionMemo = memo(ReconciliationCollapsableSection);

// Constants
const NO_FLEET_FILTER = ALL_FILTERS.filter((filter) => filter !== "Fleet");

export default function Reconciliation(props: ReconciliationProps) {
	const { locationId, selectedPayPeriod } = props;

	const queryClient = useQueryClient();

	const { titleExtended } = selectedPayPeriod ?? { titleExtended: "" };

	const { commissionCycleId, payPeriodId } = useSelectionContext();
	const RECONCILIATION_STATUS_QUERY_KEY = ["inventory", "getReconciliationStatus", locationId, commissionCycleId, payPeriodId];

	const { commissionCycle, payPeriodByDate } = useCommissionCyclesData({ date: new Date() });

	const payPeriodExtendedTitle = titleExtended?.split(" ").slice(0, 2).join(" ");
	const sectionTitle = `${payPeriodExtendedTitle} Inventory Reconciliation Summary`;

	const [searchValue, setSearchValue] = useState<string>("");
	const [globalSearch, setGlobalSearch] = useState<string>("");
	const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);
	const [exportReconciliationError, setExportReconciliationError] = useState<string>("");

	/********************************************* QUERIES ************************************************/

	const [
		{ data: reasons, isLoading: isReasonsLoading },
		{ data: resolutions, isLoading: isResolutionsLoading },
		{ data: reconciliationStatus, isLoading: isLoadingReconciliationStatus },
	] = useQueries({
		queries: [
			{
				queryKey: ["getReconciliationReasons"],
				queryFn: async () => {
					return getReconciliationReasons(locationId);
				},
				staleTime: Infinity,
				gcTime: Infinity,
			},
			{
				queryKey: ["getReconciliationResolutions"],
				queryFn: async () => {
					return getReconciliationResolutions(locationId);
				},
				staleTime: Infinity,
				gcTime: Infinity,
			},
			{
				queryKey: RECONCILIATION_STATUS_QUERY_KEY,
				queryFn: () => getReconciliationStatus(locationId, commissionCycleId, payPeriodId),
				staleTime: Infinity,
				gcTime: Infinity,
			},
		],
	});

	const [{ data: newSummaryData, isLoading: isLoadingNewSummaryData }, { data: usedSummaryData, isLoading: isLoadingUsedSummaryData }] =
		useQueries({
			queries: [
				{
					queryKey: ["inventory", "summaryData", "New", locationId, commissionCycleId, payPeriodId],
					queryFn: () =>
						getReconciliationSummary({
							locationId,
							payPeriodId,
							vehicleType: "New",
							commissionCycleId,
						}),
					staleTime: Infinity,
					gcTime: Infinity,
				},
				{
					queryKey: ["inventory", "summaryData", "Used", locationId, commissionCycleId, payPeriodId],
					queryFn: () =>
						getReconciliationSummary({
							locationId,
							payPeriodId,
							vehicleType: "Used",
							commissionCycleId,
						}),
					staleTime: Infinity,
					gcTime: Infinity,
				},
			],
		});

	useReconciliationStatusSubscription({ locationId, commissionCycleId, payPeriodId });
	useReconciliationSummarySubscription({ locationId, commissionCycleId, payPeriodId });

	const PAGINATION_SIZE = 1000;

	const {
		data,
		isLoading: isLoadingReconciliations,
		hasNextPage,
	} = usePaginatedResource({
		queryKey: ["inventory", "reconciliation", locationId, commissionCycleId, selectedPayPeriod?.payPeriodId],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}
			const paginatedResponse = await getReconciliationData(locationId, commissionCycleId, selectedPayPeriod.payPeriodId, {
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!paginatedResponse) {
				throw new Error("Error retrieving reconciliation data");
			}

			return paginatedResponse;
		},
		enabled: and(!!locationId, !!commissionCycleId, !!selectedPayPeriod?.payPeriodId),
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		initialPageParam: { after: "", first: PAGINATION_SIZE },
		staleTime: Infinity,
		gcTime: Infinity,
		fetchAllPages: true,
	});

	const reconciliations = useFilteredData({
		data,
		currentOrigin: InventoryTab.RECONCILIATION,
		pathToVinNumber: "vehicle.vinNumber",
		commissionCycleId,
		payPeriodId,
	});

	/********************************************* MUTATIONS ************************************************/
	const { mutate: mutateExportReconciliation, isPending } = useMutation({
		mutationKey: ["exportReconciliation", locationId, commissionCycleId, selectedPayPeriod?.payPeriodId],
		mutationFn: (vehicleTypes: string[]) => {
			// Call API to export reconciliation
			return exportReconciliation(locationId, commissionCycleId, selectedPayPeriod.payPeriodId, vehicleTypes);
		},
		onSuccess: (resultData) => {
			//Get Presigned Urls and Download files
			if (resultData) {
				downloadFiles(resultData);
			}
			setExportReconciliationError("");
			handleExportDialogOnOpenChange(false);
		},
		onError: () => {
			setExportReconciliationError("Failed to export reconciliation.");
		},
	});

	const { mutate: mutatePerformReconciliation, isPending: isPendingReconciliation } = useMutation({
		mutationKey: ["performReconciliation", locationId, commissionCycleId, selectedPayPeriod?.payPeriodId],
		onMutate: (input: TReconciliationType[]) => {
			optimisticUpdateReconciliationStatus(input);
		},
		mutationFn: (input: TReconciliationType[]) =>
			performReconciliation({
				locationId,
				commissionCycleId,
				payPeriodId: selectedPayPeriod.payPeriodId,
				types: input,
			}),
		onSettled(_, error) {
			if (error) {
				queryClient.invalidateQueries({ queryKey: RECONCILIATION_STATUS_QUERY_KEY });
			}
		},
	});

	/********************************************* SUBSCRIPTIONS ********************************************/

	useReconciliationSubscriptions({
		locationId,
		commissionCycleId,
		payPeriodId: selectedPayPeriod?.payPeriodId,
	});

	/********************************************* FUNCTIONS ************************************************/
	const globalSearchMemoFn = useCallback(
		debounceFn((p: string) => setGlobalSearch(p), DEBOUNCE_DELAY_500),
		[]
	);

	function handleSearch(value: string) {
		setSearchValue(value);
		globalSearchMemoFn(value);
	}

	const {
		unitsOnLotMemo,
		unitsOffLotMemo,
		unitsOnScheduleMemo,
		unitsOnMfgMemo,
		scheduleNotOnLotMemo,
		lotNotScheduledMemo,
		extraTablePropsMemo,
		onLotNotOnMfgMemo,
		onMfgNotOnLotMemo,
	} = useMemo<MemoDataReconciliation>(() => {
		const unitsOnLot = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[
					ReconciliationStatus.UNITS_ON_MFG,
					ReconciliationStatus.UNITS_ON_LOT,
					ReconciliationStatus.ON_LOT_NOT_ON_MFG,
					ReconciliationStatus.ON_LOT_NOT_ON_SCH,
				],
				ReconciliationStatus.UNITS_ON_LOT
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			lotProperties: ["name"],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES, "balance"],
		});

		const unreconciledUnitsOnLot = unitsOnLot.filter((unit) => !unit.reconciled).length;

		const unitsOffLot = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[
					ReconciliationStatus.UNITS_OFF_LOT,
					ReconciliationStatus.ON_MFG_NOT_ON_LOT,
					ReconciliationStatus.ON_SCH_NOT_ON_LOT,
					ReconciliationStatus.OFF_LOT_NOT_ON_MFG,
					ReconciliationStatus.OFF_LOT_NOT_ON_SCH,
				],
				ReconciliationStatus.UNITS_OFF_LOT
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			lotProperties: ["name"],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES, "balance"],
		});

		const unitsOffLotUnreconciled = unitsOffLot.filter((unit) => !unit.reconciled).length;

		const unitsOnSchedule = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[
					ReconciliationStatus.UNITS_ON_SCHEDULE,
					ReconciliationStatus.UNITS_ON_MFG,
					ReconciliationStatus.UNITS_ON_LOT,
					ReconciliationStatus.ON_SCH_NOT_ON_LOT,
					ReconciliationStatus.ON_MFG_NOT_ON_LOT,
				],
				ReconciliationStatus.UNITS_ON_SCHEDULE
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			lotProperties: ["name"],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES, "balance"],
		});

		const unreconciledUnitsOnSchedule = unitsOnSchedule.filter((unit) => !unit.reconciled).length;

		const unitsOnMfg = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[ReconciliationStatus.UNITS_ON_MFG, ReconciliationStatus.ON_MFG_NOT_ON_LOT],
				ReconciliationStatus.UNITS_ON_MFG
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			lotProperties: ["name"],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES, "balance"],
		});

		const unitsOnMfgUnreconciled = unitsOnMfg.filter((unit) => !unit.reconciled).length;

		const onScheduleNotOnLot = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[ReconciliationStatus.ON_SCH_NOT_ON_LOT, ReconciliationStatus.ON_MFG_NOT_ON_LOT],
				ReconciliationStatus.ON_SCH_NOT_ON_LOT
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES, "balance"],
		});

		const onScheduleNotOnLotUnreconciled = onScheduleNotOnLot.filter((unit) => !unit.reconciled).length;

		const onLotNotScheduled = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[ReconciliationStatus.ON_LOT_NOT_ON_SCH, ReconciliationStatus.ON_LOT_NOT_ON_MFG],
				ReconciliationStatus.ON_LOT_NOT_ON_SCH
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			lotProperties: ["name"],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES],
		});

		const onLotNotScheduledUnreconciled = onLotNotScheduled.filter((unit) => !unit.reconciled).length;

		const onLotNotOnMfg = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[ReconciliationStatus.ON_LOT_NOT_ON_MFG],
				ReconciliationStatus.ON_LOT_NOT_ON_MFG
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			lotProperties: ["name"],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES],
		});

		const onLotNotOnMfgUnreconciled = onLotNotOnMfg.filter((unit) => !unit.reconciled).length;

		const onMfgNotOnLot = filterByProperties({
			rows: reconciliationFilterByType(
				reconciliations,
				[ReconciliationStatus.ON_MFG_NOT_ON_LOT],
				ReconciliationStatus.ON_MFG_NOT_ON_LOT
			),
			search: globalSearch,
			vehicleProperties: [...VEHICLE_PROPERTIES],
			reconciliationProperties: [...REASONS_RESOLUTION_PROPERTIES],
		});

		const onMfgNotOnLotUnreconciled = onMfgNotOnLot.filter((unit) => !unit.reconciled).length;

		return {
			unitsOnLotMemo: {
				data: unitsOnLot,
				unreconciled: unreconciledUnitsOnLot,
				filters: ALL_FILTERS,
				openCollapse: handleOpenCollapse(unitsOnLot.length),
			},
			unitsOffLotMemo: {
				data: unitsOffLot,
				unreconciled: unitsOffLotUnreconciled,
				filters: ALL_FILTERS,
				openCollapse: handleOpenCollapse(unitsOffLot.length),
			},
			unitsOnScheduleMemo: {
				data: unitsOnSchedule,
				unreconciled: unreconciledUnitsOnSchedule,
				filters: UNITS_ON_SCHEDULE_FILTER,
				openCollapse: handleOpenCollapse(unitsOnSchedule.length),
			},
			unitsOnMfgMemo: {
				data: unitsOnMfg,
				unreconciled: unitsOnMfgUnreconciled,
				filters: UNITS_ON_MFG_FILTER,
				openCollapse: handleOpenCollapse(unitsOnMfg.length),
			},
			scheduleNotOnLotMemo: {
				data: onScheduleNotOnLot,
				unreconciled: onScheduleNotOnLotUnreconciled,
				filters: NO_FLEET_FILTER,
				openCollapse: handleOpenCollapse(onScheduleNotOnLot.length),
			},
			lotNotScheduledMemo: {
				data: onLotNotScheduled,
				filters: NO_FLEET_FILTER,
				unreconciled: onLotNotScheduledUnreconciled,
				openCollapse: handleOpenCollapse(onLotNotScheduled.length),
			},
			onLotNotOnMfgMemo: {
				data: onLotNotOnMfg,
				filters: NO_PREOWNED_FILTER,
				unreconciled: onLotNotOnMfgUnreconciled,
				openCollapse: handleOpenCollapse(onLotNotOnMfg.length),
			},
			onMfgNotOnLotMemo: {
				data: onMfgNotOnLot,
				filters: NO_PREOWNED_FILTER,
				unreconciled: onMfgNotOnLotUnreconciled,
				openCollapse: handleOpenCollapse(onMfgNotOnLot.length),
			},
			extraTablePropsMemo: {
				reasons: { data: reasons ?? [], isLoading: isReasonsLoading },
				resolutions: { data: resolutions ?? [], isLoading: isResolutionsLoading },
			},
		};
	}, [reconciliations, globalSearch, reasons, resolutions]);

	function isCurrentPayPeriod() {
		return !(commissionCycleId === commissionCycle?._id && payPeriodId === payPeriodByDate?._id);
	}

	const disableReconcileNowButton = or(isLoadingReconciliationStatus, isCurrentPayPeriod());

	function optimisticUpdateReconciliationStatus(types: TReconciliationType[]) {
		queryClient.setQueriesData(
			{ queryKey: RECONCILIATION_STATUS_QUERY_KEY },
			(cachedData: IReconciliationStatus | null | undefined) => {
				if (!types.length) {
					return cachedData;
				}

				const defaultObjCached = !cachedData
					? ({ _id: uuidv4(), commissionCycleId, locationId, payPeriodId } as IReconciliationStatus)
					: cloneDeep(cachedData);

				return types.reduce(
					(cached, type) => ({
						...cached,
						[`${type.toLowerCase()}VehicleReconciliation`]: {
							status: "RUNNING",
							startDate: new Date(),
							endDate: null,
							error: "",
						},
					}),
					defaultObjCached
				);
			}
		);
	}
	/********************************************* HANDLERS ************************************************/
	const handleExportDialogOnOpenChange = (isOpen: boolean) => {
		setIsExportDialogOpen(isOpen);
	};

	/********************************************* RENDER COMPONENT ************************************************/
	if (or(isLoadingReconciliations, !locationId, !selectedPayPeriod?.payPeriodId, isLoadingNewSummaryData, isLoadingUsedSummaryData)) {
		return <ReconciliationSkeleton />;
	}

	return (
		<>
			<Stack component={"article"}>
				{/********************************************** HEADER SECTION **********************************************/}
				<Stack direction="row" justifyContent={"space-between"} alignItems={"center"} mt={4} component={"header"}>
					<Typography variant={"h2"} className="!font-montserrat !text-default !font-medium !text-2xl">
						{sectionTitle}
					</Typography>
					<Button
						variant="contained"
						disableElevation
						disabled={!reconciliations?.length}
						className={clsx("!rounded-md !h-[30px] !w-[144px] !font-sans !text-sm !text-white !font-normal !normal-case", {
							"!bg-primary hover:!shadow-primary-shadow-50": !!reconciliations?.length,
							"!bg-disabled !fill-white": !reconciliations?.length,
						})}
						onClick={() => handleExportDialogOnOpenChange(true)}
					>
						Export Reconciliation
					</Button>
				</Stack>
				{/********************************************** SUMMARY TABLES **********************************************/}
				<Grid container mt={3.5} columnSpacing={6} component={"section"}>
					<Grid item xs={6}>
						<SummaryTableMemo vehicleType={"New"} title={"New Vehicles"} data={newSummaryData ?? ({} as ISummaryDetail)} />
					</Grid>
					<Grid item xs={6}>
						<SummaryTableMemo
							vehicleType={"Used"}
							title={"Pre-Owned Vehicles"}
							data={usedSummaryData ?? ({} as ISummaryDetail)}
						/>
					</Grid>
				</Grid>
				<Divider />
				<Grid container mt={4} columnSpacing={6} component={"section"}>
					<Grid item xs={12} justifyContent="right" display={"flex"}>
						<SearchInput
							value={searchValue}
							onChange={handleSearch}
							clearSearch={() => {
								setSearchValue("");
								setGlobalSearch("");
							}}
							className="w-[210px]"
						/>
					</Grid>
				</Grid>
				{/********************************************** COLLAPSABLE SECTIONS **********************************************/}
				{/*	Units On Lot */}
				<CollapseArea
					title={`Units on Lot ${buildTextForUnreconciled(unitsOnLotMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={unitsOnLotMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={UnitsOnLotTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={unitsOnLotMemo.data}
						filters={unitsOnLotMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/*	Units Off Lot */}
				<CollapseArea
					title={`Units off Lot ${buildTextForUnreconciled(unitsOffLotMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={unitsOffLotMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={UnitsOffLotTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={unitsOffLotMemo.data}
						filters={unitsOffLotMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/*	Units On Schedule */}
				<CollapseArea
					title={`Units on Schedule ${buildTextForUnreconciled(unitsOnScheduleMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={unitsOnScheduleMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={UnitsOnScheduleTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={unitsOnScheduleMemo.data}
						filters={unitsOnScheduleMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/*	Units On Mfg */}
				<CollapseArea
					title={`Units on Mfg ${buildTextForUnreconciled(unitsOnMfgMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={unitsOnMfgMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={UnitsOnMfgTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={unitsOnMfgMemo.data}
						filters={unitsOnMfgMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/*On Schedule Not On lot*/}
				<CollapseArea
					title={`On Sch Not On Lot ${buildTextForUnreconciled(scheduleNotOnLotMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={scheduleNotOnLotMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={OnScheduleNotOnLotTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={scheduleNotOnLotMemo.data}
						filters={scheduleNotOnLotMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/* On Lot Not On Schedule */}
				<CollapseArea
					title={`On Lot Not On Sch ${buildTextForUnreconciled(lotNotScheduledMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={lotNotScheduledMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={OnLotNotScheduledTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={lotNotScheduledMemo.data}
						filters={lotNotScheduledMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/* On Lot Not On Mfg */}
				<CollapseArea
					title={`On Lot Not On MFG ${buildTextForUnreconciled(onLotNotOnMfgMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={onLotNotOnMfgMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={OnLotNotOnMfgTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={onLotNotOnMfgMemo.data}
						filters={onLotNotOnMfgMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				{/* On Mfg Not On Lot */}
				<CollapseArea
					title={`On Mfg Not On Lot ${buildTextForUnreconciled(onMfgNotOnLotMemo.unreconciled)}`}
					collapsible
					sx={collapseAreaSx}
					openCollapse={onMfgNotOnLotMemo.openCollapse}
					autoCollapse
				>
					<ReconciliationCollapsableSectionMemo
						Component={OnMfgNotOnLotTable}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={selectedPayPeriod.payPeriodId}
						data={onMfgNotOnLotMemo.data}
						filters={onMfgNotOnLotMemo.filters}
						extraTableProps={extraTablePropsMemo}
						hasNextPage={hasNextPage}
					/>
				</CollapseArea>
				<Divider />
				<ReconcileNowButton
					disabled={disableReconcileNowButton}
					reconciliationStatus={reconciliationStatus}
					isPendingReconciliation={isPendingReconciliation}
					onReconcile={mutatePerformReconciliation}
				/>
			</Stack>
			<ExportReconciliationDialog
				isOpen={isExportDialogOpen}
				onClose={() => handleExportDialogOnOpenChange(false)}
				mutateExportReconciliation={mutateExportReconciliation}
				isPending={isPending}
				exportReconciliationError={exportReconciliationError}
			/>
		</>
	);
}
